import { StatusDefinition, allowedStatuses } from './Status'

export enum CollectionMethod {
    LOCAL = 'local',
    SWIFT = 'swift',
}

export enum CollectionProvider {
    DBS_SG = 'dbs_sg',
    DBS_HK = 'dbs_hk',
    WALLEX_HK_DBS_HK = 'wallex_hk_dbs_hk',
}

export interface CollectionPayload {
    senderName: string
    senderAccountNumber: string
    senderBankCountry?: string
    senderBankBIC?: string
    receiverName: string
    receiverAccountNumber: string
    amount: number
    currency: string
    method: CollectionMethod | string
    customerReference: string
}

export interface CollectionRetrieve {
    id: string
    accountId: string
    collectionAccountId: string
    status: string
    reference: string
    provider?: string
    accountNumber: string
    name?: string
}

export interface CollectionUpdatePayload {
    status: string
    accountId: string
    collectionAccountId: string
}

export type CollectionStatus = StatusDefinition

export default class Collection {
    static journey = ['pending', 'completed']

    static hints: Record<string, string> = {
        pending: 'Held for compliance review',
        completed: 'Approved, funds are credited to the account',
        rejected: 'Declined by compliance, funds are not credited',
    }

    static accountHints: Record<string, string> = {
        inactive: 'Requested, not yet set up by the bank',
        pending: 'Acknowledged by the bank, waiting for activation',
        active: 'Ready to receive collections',
        enabled: 'Ready to receive collections',
        disabled: 'Closed, can no longer receive collections',
    }

    static accountJourney(provider?: string): string[] {
        if (provider === CollectionProvider.DBS_SG) return ['inactive', 'pending', 'active']
        if (provider === CollectionProvider.DBS_HK || provider === CollectionProvider.WALLEX_HK_DBS_HK) return ['inactive', 'active']
        return []
    }

    static statuses(collection: CollectionRetrieve): CollectionStatus[] {
        const statuses: Array<CollectionStatus> = [
            {
                value: 'pending',
                description: 'Pending',
                available: [],
            },
            {
                value: 'completed',
                description: 'Completed',
                available: ['pending'],
            },
            {
                value: 'rejected',
                description: 'Rejected',
                available: ['pending'],
            },
        ]

        return allowedStatuses(statuses, collection.status)
    }

    private static getAccountStatusByProvider(provider?: string): Array<CollectionStatus> {
        switch (provider) {
            case CollectionProvider.DBS_SG:
                return [
                    {
                        value: 'active',
                        description: 'Active',
                        available: ['pending'],
                    },
                    {
                        value: 'pending',
                        description: 'Pending',
                        available: ['inactive'],
                    },
                    {
                        value: 'inactive',
                        description: 'Inactive',
                        available: [],
                    },
                    {
                        value: 'disabled',
                        description: 'Disabled',
                        available: ['active', 'pending', 'inactive'],
                    },
                ]
            case CollectionProvider.DBS_HK:
            case CollectionProvider.WALLEX_HK_DBS_HK:
                return [
                    {
                        value: 'active',
                        description: 'Active',
                        available: ['inactive'],
                    },
                    {
                        value: 'inactive',
                        description: 'Inactive',
                        available: [],
                    },
                    {
                        value: 'disabled',
                        description: 'Disabled',
                        available: ['active', 'pending', 'inactive'],
                    },
                ]
            default:
                return [
                    {
                        value: 'disabled',
                        description: 'Disabled',
                        available: ['active', 'inactive', 'pending', 'enabled'],
                    },
                    {
                        value: 'inactive',
                        description: 'Inactive',
                        available: [],
                    },
                    {
                        value: 'active',
                        description: 'Active',
                        available: [],
                    },
                    {
                        value: 'pending',
                        description: 'Pending',
                        available: [],
                    },
                    {
                        value: 'enabled',
                        description: 'Enabled',
                        available: [],
                    },
                ]
        }
    }

    static accountStatuses(collection: { status: string; provider?: string }): CollectionStatus[] {
        const statuses = this.getAccountStatusByProvider(collection?.provider)

        return allowedStatuses(statuses, collection.status)
    }
}
